import React from "react";
import MutedText from "../../ui/MutedText";
import { useSensorChart, useSensorDiagnostics } from "./useSensorChart";

const CHART_W = 340;
const CHART_H = 72;
const CHART_PAD = 10;
const CHANNEL_COLORS = ["#60a5fa", "#34d399", "#fb923c", "#f472b6", "#a78bfa", "#facc15"];

function buildLinePath(points, width, height, padding) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  let yMin = Math.min(...ys);
  let yMax = Math.max(...ys);
  if (yMin === yMax) {
    const pad = Math.abs(yMin) > 1 ? Math.abs(yMin) * 0.05 : 0.5;
    yMin -= pad;
    yMax += pad;
  } else {
    const pad = (yMax - yMin) * 0.12;
    yMin -= pad;
    yMax += pad;
  }
  const uw = width - padding * 2;
  const uh = height - padding * 2;
  const xSpan = Math.max(1e-9, xMax - xMin);
  const coords = points.map((p) => ({
    x: padding + ((p.x - xMin) / xSpan) * uw,
    y: height - padding - ((p.y - yMin) / (yMax - yMin)) * uh,
  }));
  return {
    path: coords
      .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
      .join(" "),
    yMin,
    yMax,
  };
}

function formatVal(v) {
  if (!Number.isFinite(v)) return "—";
  if (Math.abs(v) >= 1000) return v.toFixed(1);
  if (Math.abs(v) >= 10) return v.toFixed(2);
  return v.toFixed(4);
}

const SensorChart = React.memo(({ sensorName }) => {
  const channels = useSensorChart(sensorName);
  const diagnostics = useSensorDiagnostics(sensorName);
  const hasData = channels.some((ch) => ch.length >= 2);
  const emptyMessage = diagnostics.workerDiagnostics?.sensorsRemoved
    ? "Sensor data unavailable - sensors were stripped by the physics fallback"
    : diagnostics.isRunning
      ? "No live sensor data from physics"
      : "Start physics to view sensor data";

  if (!hasData) {
    return (
      <div className="rounded border border-gray-700 bg-gray-900/60 px-2 py-2">
        <div className="flex h-10 items-center justify-center">
          <MutedText className="text-[11px] text-center">{emptyMessage}</MutedText>
        </div>
      </div>
    );
  }

  const latestValues = channels.map((ch) => ch[ch.length - 1]?.y ?? null);

  return (
    <div className="rounded border border-gray-700 bg-gray-900/60 px-1 pb-1 pt-0.5">
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="w-full"
        style={{ height: CHART_H }}
        preserveAspectRatio="none"
      >
        {channels.map((pts, ch) => {
          const result = buildLinePath(pts, CHART_W, CHART_H, CHART_PAD);
          if (!result) return null;
          return (
            <path
              key={ch}
              d={result.path}
              fill="none"
              stroke={CHANNEL_COLORS[ch % CHANNEL_COLORS.length]}
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-x-3 px-1">
        {latestValues.map((v, ch) => (
          <span
            key={ch}
            className="text-[10px]"
            style={{ color: CHANNEL_COLORS[ch % CHANNEL_COLORS.length] }}
          >
            {channels.length > 1 ? `[${ch}] ` : ""}
            {formatVal(v)}
          </span>
        ))}
      </div>
    </div>
  );
});

export default SensorChart;
